<template>
  <v-container fluid class="pa-0">

    <v-snackbar
        v-model="showSnackbar"
        :color="snackbarColor"
        :timeout="3000"
        location="top"
        rounded="pill"
        elevation="4"
        class="pa-3"
    >
      <div class="d-flex align-center">
        <v-icon class="me-3" :icon="snackbarIcon"></v-icon>
        <span class="text-subtitle-1 font-weight-medium">{{ snackbarText }}</span>
      </div>
      <template v-slot:actions>
        <v-btn
            variant="text"
            icon="mdi-close"
            @click="showSnackbar = false"
        ></v-btn>
      </template>
    </v-snackbar>

    <v-dialog v-model="showDeleteConfirm" max-width="400">
      <v-card class="rounded-xl elevation-5">
        <v-card-title class="text-h5 bg-error text-white rounded-t-xl py-4">
          <v-icon icon="mdi-delete-alert" class="me-2"></v-icon>
          {{ t('focusgroup.deleteFocusGroup') }}
        </v-card-title>
        <v-card-text class="pa-6 text-body-1">
          {{ t('focusgroup.areYouSureYouWantToDeleteThisFocusGroupThisActionCannotBeUndone') }}
        </v-card-text>
        <v-card-actions class="pa-4 pb-6">
          <v-spacer></v-spacer>
          <v-btn color="grey-darken-1" variant="text" @click="showDeleteConfirm = false" rounded="pill" class="px-5">
            {{ t('generic.cancel') }}
          </v-btn>
          <v-btn
              color="error"
              variant="elevated"
              @click="confirmDeleteFocusGroup"
              rounded="pill"
              class="px-5"
              :loading="deletingFocusGroup"
          >
            <v-icon start icon="mdi-delete" class="me-1"></v-icon>
            {{ t('generic.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-sheet
        color="primary"
        class="py-8 mb-8"
        rounded="0"
        elevation="1"
    >
      <v-row justify="center" class="text-center mt-2">
        <v-icon icon="mdi-account-group" size="56" color="white" class="mb-4 mr-2"></v-icon>
        <h1 class="text-h3 font-weight-light text-white">{{ t('focusgroup.focusGroupAnalysis') }}</h1>
      </v-row>
      <v-row justify="center" class="text-center">
        <div class="text-subtitle-1 text-white mt-2">{{ t('focusgroup.viewAndAnalyzeFocusGroupConversationsAndInsights') }}</div>
      </v-row>
    </v-sheet>

    <v-row class="mt-6" justify="center">
      <v-col cols="12" :md="selectedFocusGroup ? 4 : 6">
        <v-card rounded="xl" elevation="2" class="border ml-4">
          <v-card-title class="d-flex align-center px-6 pt-5 pb-2">
            <v-icon icon="mdi-format-list-bulleted" color="primary" class="me-3"></v-icon>
            <span class="text-h6">{{ t('focusgroup.focusGroups') }}</span>
          </v-card-title>

          <v-card-text class="px-6 pb-2">
            <v-select
                class="domain-select mb-2 mt-2"
                :label="$t('generic.domain')"
                :items="domainOptions"
                item-title="title"
                item-value="value"
                v-model="selectedDomain"
                density="default"
                variant="outlined"
                color="primary"
                prepend-inner-icon="mdi-domain"
                hide-details
            />
            <v-progress-circular
                v-if="isCampaignsLoading"
                indeterminate
                color="primary"
                class="mx-auto d-block my-4"
            ></v-progress-circular>

            <!-- Scoped to just this autocomplete (not global Vuetify config)
                 so the built-in empty-state text ("No data available" etc.)
                 follows the app locale without affecting any other page.
                 Vuetify's own en/ja message bundles are passed explicitly
                 since the root Vuetify config only registers "en". -->
            <v-locale-provider :locale="$i18n.locale" :messages="{ en: vuetifyEn, ja: vuetifyJa }">
            <v-autocomplete
                v-show="!isCampaignsLoading"
                v-model="selectedCampaign"
                :items="dropdownCampaigns"
                :label="$t('focusgroup.filterByTopic')"
                variant="outlined"
                density="comfortable"
                hide-details
                clearable
                class="rounded-lg"
            >
              <template v-slot:prepend-item>
                <v-list-item prepend-icon="mdi-filter-variant" :title="$t('focusgroup.topics')" class="text-primary"></v-list-item>
                <v-divider></v-divider>
              </template>
              <template v-slot:prepend-inner>
                <v-icon icon="mdi-magnify" color="primary"></v-icon>
              </template>
            </v-autocomplete>
            </v-locale-provider>
          </v-card-text>

          <v-card-subtitle class="px-6 pt-4 pb-0 d-flex align-center">
            <span class="font-weight-medium">{{ t('focusgroup.availableSessions') }}</span>
            <v-chip
                v-if="selectedCampaign"
                color="primary"
                size="small"
                class="ml-2 px-2"
                variant="tonal"
            >
              {{ filteredFocusGroups.length }}
            </v-chip>
          </v-card-subtitle>

          <v-divider class="mx-6 my-3"></v-divider>

          <v-card-text class="px-4 py-0 mb-4">
            <div v-if="isLoadingFocusGroups" class="d-flex justify-center my-4">
              <v-progress-circular
                  indeterminate
                  color="primary"
              ></v-progress-circular>
            </div>

            <v-list v-else class="focus-groups-list py-0 bg-transparent">
              <v-list-item
                  v-for="(group, index) in filteredFocusGroups"
                  :key="index"
                  @click="handleFocusGroupClick(group)"
                  rounded="lg"
                  :class="{'v-list-item--active': group === selectedGroup}"
                  :color="group === selectedGroup ? 'primary' : ''"
                  class="mb-2 focus-group-item"
              >
                <template v-slot:prepend>
                  <v-avatar size="36" color="primary" variant="tonal" class="mr-3">
                    <v-icon icon="mdi-account-group" size="20"></v-icon>
                  </v-avatar>
                </template>
                <v-list-item-title class="font-weight-medium text-body-1">{{ group.name }}</v-list-item-title>
                <v-list-item-subtitle class="text-caption mt-1 d-flex align-center">
                  <v-icon icon="mdi-clock-outline" size="14" class="mr-1"></v-icon>
                  {{ formatTimestamp(group.timestamp) }}
                  <v-chip
                      v-if="group.execution_mode === 'template_based'"
                      color="primary"
                      variant="tonal"
                      size="x-small"
                      class="ml-2"
                  >
                    <v-icon start icon="mdi-file-tree-outline" size="12"></v-icon>
                    {{ t('focusgroup.templateBadge') }}
                  </v-chip>
                </v-list-item-subtitle>
                <template v-slot:append>
                  <v-btn
                      icon="mdi-delete-outline"
                      color="error"
                      size="small"
                      variant="text"
                      @click.stop="promptDeleteFocusGroup(group)"
                  ></v-btn>
                </template>
              </v-list-item>

              <v-list-item
                  v-if="filteredFocusGroups.length === 0"
                  class="my-4 text-center"
                  disabled
              >
                <v-list-item-title class="text-medium-emphasis">
                  <v-icon icon="mdi-information-outline" class="mb-1 mr-1"></v-icon>
                  {{ t('focusgroup.noFocusGroupsAvailable') }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="8" v-if="selectedFocusGroup || isLoadingFocusGroupDetails">
        <v-card
            v-if="isLoadingFocusGroupDetails"
            elevation="2"
            rounded="xl"
            class="focus-group-details-card border mr-4 pa-6"
            height="400"
        >
          <div class="d-flex flex-column justify-center align-center h-100">
            <v-progress-circular
                indeterminate
                color="primary"
                size="64"
            ></v-progress-circular>
            <span class="text-body-1 text-medium-emphasis mt-4">{{ t('focusgroup.loadingFocusGroupDetails') }}</span>
          </div>
        </v-card>

        <v-card
            v-else-if="selectedFocusGroup"
            elevation="2"
            rounded="xl"
            class="focus-group-details-card border mr-4"
        >
          <v-card-title class="card-title px-6 py-5 d-flex justify-space-between">
            <div class="d-flex align-center">
              <v-avatar color="primary" size="40" class="mr-4">
                <v-icon icon="mdi-information" size="24"></v-icon>
              </v-avatar>
              <div>
                <div class="text-h5 font-weight-medium">{{ selectedGroup?.name }}</div>
                <div class="text-caption text-medium-emphasis mt-1">
                  {{ t('focusgroup.sessionFrom') }} {{ formatTimestamp(selectedGroup?.timestamp) }}
                </div>
                <div
                    v-if="selectedFocusGroup.template_name"
                    class="text-caption text-medium-emphasis mt-1 d-flex align-center"
                >
                  <v-icon icon="mdi-file-tree-outline" size="14" class="mr-1"></v-icon>
                  {{ t('focusgroup.template') }} {{ selectedFocusGroup.template_name }}
                </div>
              </div>
            </div>

            <v-chip color="primary" variant="tonal" class="px-3">
              <v-icon start icon="mdi-account" size="16"></v-icon>
              {{ selectedFocusGroup.persona_ids?.length || 0 }} {{ t('focusgroup.personas') }}
            </v-chip>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text class="pa-6">
            <!-- Template runs only, [] without files. The stored references have no
                 links (they would expire): each preview fetches fresh ones. -->
            <div v-if="stimulusMaterials.length" class="stimulus-material pa-4 mb-4">
              <div class="d-flex align-center mb-3">
                <v-avatar color="primary" variant="tonal" size="32" class="mr-3">
                  <v-icon icon="mdi-paperclip" size="16"></v-icon>
                </v-avatar>
                <span class="font-weight-medium">{{ t('focusgroup.stimulusMaterials') }}</span>
              </div>
              <attachment-preview
                  v-for="material in stimulusMaterials"
                  :key="`${material.position}-${material.attachment.file_id}`"
                  :attachment="material.attachment"
                  :position="material.position"
                  :total="material.total"
                  class="stimulus-material-item"
              />
            </div>

            <v-expansion-panels variant="accordion" class="custom-panels">
              <v-expansion-panel class="expansion-panel mb-4" elevation="0">
                <v-expansion-panel-title class="panel-title px-4 py-3">
                  <div class="d-flex align-center">
                    <v-avatar color="primary" variant="tonal" size="32" class="mr-3">
                      <v-icon icon="mdi-text-box" size="16"></v-icon>
                    </v-avatar>
                    <span class="font-weight-medium">{{ t('focusgroup.generalSummary') }}</span>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text class="panel-text px-4 py-4">
                  <div
                      v-if="displaySummary && displaySummary.general_summary"
                      class="markdown-content"
                  >
                    <div v-html="convertMarkdown(displaySummary.general_summary)"></div>
                  </div>
                  <div v-else class="no-data">
                    <v-icon icon="mdi-information-outline" class="mr-2"></v-icon>
                    {{ t('focusgroup.noGeneralSummaryAvailable') }}
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel
                  v-for="(persona, index) in Object.values(selectedFocusGroup.persona_names || {})"
                  :key="index"
                  class="expansion-panel mb-3"
                  elevation="0"
              >
                <v-expansion-panel-title class="panel-title px-4 py-3">
                  <div class="d-flex align-center">
                    <v-avatar color="primary" variant="tonal" size="32" class="mr-3">
                      <v-icon icon="mdi-account" size="16"></v-icon>
                    </v-avatar>
                    <span class="font-weight-medium">{{ persona }} {{ t('focusgroup.summary') }}</span>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text class="panel-text px-4 py-4">
                  <div v-if="getPersonaSummary(persona)" class="markdown-content">
                    <div v-html="convertMarkdown(getPersonaSummary(persona))"></div>
                  </div>
                  <div v-else class="no-data">
                    <v-icon icon="mdi-information-outline" class="mr-2"></v-icon>
                    {{ t('focusgroup.noSummaryAvailableFor') }} {{ persona }} {{ t('focusgroup.persona') }}.
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel class="expansion-panel mb-3" elevation="0">
                <v-expansion-panel-title class="panel-title px-4 py-3">
                  <div class="d-flex align-center">
                    <v-avatar color="primary" variant="tonal" size="32" class="mr-3">
                      <v-icon icon="mdi-text-box" size="16"></v-icon>
                    </v-avatar>
                    <span class="font-weight-medium">{{ t('focusgroup.improvementPlan') }}</span>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text class="panel-text px-4 py-4">
                  <div v-if="displayImprovementPlan" class="markdown-content" ref="improvementPlanContainer">
                    <div v-html="convertMarkdown(displayImprovementPlan)"></div>
                  </div>
                  <div v-else class="no-data">
                    <v-icon icon="mdi-information-outline" class="mr-2"></v-icon>
                    {{ t('focusgroup.noImprovementPlanAvailable') }}
                  </div>
                  <v-btn
                      prepend-icon="mdi-download"
                      color="primary"
                      variant="tonal"
                      @click="downloadPlan"
                      class="ms-2 mt-2"
                  >
                    {{ t('generic.download') }}
                  </v-btn>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-6">
            <v-btn
                color="primary"
                variant="elevated"
                rounded="lg"
                size="large"
                class="px-6"
                prepend-icon="mdi-message-text"
                @click="openFocusGroupDialog"
            >
              {{ t('focusgroup.viewConversationHistory') }}
            </v-btn>
          </v-card-actions>
        </v-card>

      </v-col>
    </v-row>

    <focus-group-dialog
        v-model:showDialog="isFocusGroupDialogVisible"
        :histories="displayHistories"
        :steps="selectedFocusGroup ? (selectedFocusGroup.steps || []) : []"
        :has-attachments="stimulusMaterials.length > 0"
        :images_url="focus_group_images"
    />
  </v-container>
</template>

<script>
import { marked } from 'marked';
import {
  fetch_campaigns,
  add_campaign,
  fetch_persona_names,
  fetch_focus_groups,
  get_focus_group,
  delete_focus_group,
  build_domain_options,
  get_default_domain,
} from "../api.js";
import CampaignDialog from "./InterviewCampaignDialog.vue";
import FocusGroupDialog from "./FocusGroupDialog.vue";
import AttachmentPreview from "./AttachmentPreview.vue";
import { resultAttachments } from "@/utils/templateAttachment";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useI18n } from "vue-i18n";
import { en as vuetifyEn, ja as vuetifyJa } from 'vuetify/locale';
import { formatDateTime } from '@/utils/dateFormat';

export default {
  components: {
    CampaignDialog,
    FocusGroupDialog,
    AttachmentPreview,
  },
  setup() {
    const { t } = useI18n();

    return {
    t,
    };
  },
  data() {
    return {
      // Vuetify's own locale message bundles, passed explicitly to the
      // page-scoped v-locale-provider below (the root Vuetify config only
      // registers "en", so "ja" must be supplied here for the topic
      // autocomplete's built-in empty-state text to localize).
      vuetifyEn,
      vuetifyJa,
      campaigns: [],
      selectedCampaign: null,
      checkboxOptions: [],
      isDialogVisible: false,
      isFocusGroupDialogVisible: false,
      isLoading: false,
      isCampaignsLoading: false,
      isLoadingFocusGroups: false,
      isLoadingFocusGroupDetails: false,
      focusGroups: [],
      selectedFocusGroup: null,
      selectedGroup: null,

      showSnackbar: false,
      snackbarText: "",
      snackbarColor: "error",
      snackbarIcon: "mdi-alert-circle",

      focus_group_images: [],
      image_data: [],
      showDeleteConfirm: false,
      focusGroupToDelete: null,
      deletingFocusGroup: false,
      // DOMAINS
      selectedDomain: get_default_domain(),
    };
  },
  computed: {
    dropdownCampaigns() {
      return this.campaigns.map((campaign) => campaign.topic_name);
    },
    filteredFocusGroups() {
      if (!this.selectedCampaign) {
        return this.focusGroups;
      }
      return this.focusGroups.filter(group => group.name === this.selectedCampaign);
    },
    domainOptions() {
      return build_domain_options(this.$t);
    },
    // Focus Group Analysis in the current UI language. Both languages are
    // stored on the record at generation time (summary_i18n /
    // improvement_plan_i18n), so switching locale is an instant reactive
    // lookup — no API call, no LLM, no loading state. Older records that
    // predate bilingual analysis have no *_i18n map and fall back to the
    // single stored `summary` / `improvement_plan`.
    displaySummary() {
      const fg = this.selectedFocusGroup;
      if (!fg) return null;
      const locale = this.$i18n.locale;
      const usedI18nKey = fg.summary_i18n && fg.summary_i18n[locale] ? locale : null;
      console.log(
        '[LANG-DIAG] displaySummary: this.$i18n.locale=%o record.language=%o used_i18n_key=%o has_summary_i18n_en=%o has_summary_i18n_ja=%o',
        locale, fg.language, usedI18nKey,
        !!(fg.summary_i18n && fg.summary_i18n.en), !!(fg.summary_i18n && fg.summary_i18n.ja),
      );
      return (fg.summary_i18n && fg.summary_i18n[locale]) || fg.summary || null;
    },
    displayImprovementPlan() {
      const fg = this.selectedFocusGroup;
      if (!fg) return null;
      const locale = this.$i18n.locale;
      return (fg.improvement_plan_i18n && fg.improvement_plan_i18n[locale])
          || fg.improvement_plan || null;
    },
    // The run's stimulus materials in run order, numbered as the group saw them.
    stimulusMaterials() {
      return resultAttachments(this.selectedFocusGroup);
    },
    // Conversation history in the current UI language — same instant, purely
    // reactive lookup as displaySummary. Falls back to the single stored
    // `histories` for records generated before bilingual conversation existed.
    displayHistories() {
      const fg = this.selectedFocusGroup;
      if (!fg) return [];
      const locale = this.$i18n.locale;
      return (fg.histories_i18n && fg.histories_i18n[locale]) || fg.histories || [];
    },
  },
  watch: {
    selectedCampaign() {
      this.selectedFocusGroup = null;
      this.selectedGroup = null;
    },
    selectedFocusGroup(newVal) {
      // Pulisci i vecchi URL prima di crearne di nuovi
      this.focus_group_images.forEach(img => {
        if (img.url && img.url.startsWith('blob:')) {
          URL.revokeObjectURL(img.url);
        }
      });

      this.focus_group_images = [];
      this.image_data = [];

      if (newVal && newVal.image_data_map) {
        console.log("Processing image_data_map:", newVal.image_data_map);

        // Itera sulle chiavi (filename) e sui valori (fileData) della mappa
        Object.entries(newVal.image_data_map).forEach(([filename, fileData]) => {
          console.log("Processing image:", filename, fileData);

          // Se abbiamo già un URL diretto dal BE, usalo
          if (fileData.url) {
            this.focus_group_images.push({
              file_id: filename,
              url: fileData.url
            });
            console.log("Added direct URL for:", filename);
          }
          // Altrimenti se abbiamo base64, crea un Blob URL
          else if (fileData.base64 && fileData.mime_type) {
            const blob = this.createBlob(fileData.base64, fileData.mime_type);
            const imageUrl = URL.createObjectURL(blob);

            this.focus_group_images.push({
              file_id: filename,
              url: imageUrl
            });
            console.log("Created Blob URL for:", filename);
          }
        });

        console.log("Prepared focus_group_images:", this.focus_group_images);
      }
    },
    selectedDomain(newValue, oldValue){
      if(newValue !== oldValue){
        this.selectedCampaign = null;
        this.fetchCampaigns(newValue);
        this.fetchPersonaNames(newValue);
        this.fetchFocusGroups(newValue);
      }
    },
  },
  methods: {
    createBlob(base64_data, mime_type){
      const clean = base64_data && base64_data.includes(',')
          ? base64_data.split(',')[1]
          : base64_data || '';

      const byteCharacters = atob(clean);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      return new Blob([byteArray], { type: mime_type });
    },
    showToast(message, type = "error") {
      this.snackbarText = message;

      if (type === "error") {
        this.snackbarColor = "error";
        this.snackbarIcon = "mdi-alert-circle";
      } else if (type === "success") {
        this.snackbarColor = "success";
        this.snackbarIcon = "mdi-check-circle";
      } else if (type === "warning") {
        this.snackbarColor = "warning";
        this.snackbarIcon = "mdi-alert";
      } else if (type === "info") {
        this.snackbarColor = "info";
        this.snackbarIcon = "mdi-information";
      }

      this.showSnackbar = true;
    },

    openDialog() {
      this.isDialogVisible = true;
    },
    async openFocusGroupDialog() {
      this.isFocusGroupDialogVisible = true;
    },
    async fetchCampaigns(selected_domain) {
      this.isCampaignsLoading = true;
      try {
        const response = await fetch_campaigns(selected_domain);
        if (response && Array.isArray(response.data)) {
          this.campaigns = response.data;
          console.log("Fetched campaigns:", this.campaigns);
        } else {
          throw new Error("Invalid response format");
        }
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        this.campaigns = [];
        this.showToast(this.$t('focusgroup.failedToLoadCampaignsPleaseTryAgainLater'), "error");
      } finally {
        this.isCampaignsLoading = false;
      }
    },
    async fetchPersonaNames(selected_domain) {
      try {
        const response = await fetch_persona_names(selected_domain);
        if (response && Array.isArray(response.data)) {
          this.checkboxOptions = response.data;
        } else {
          throw new Error("Invalid response format");
        }
      } catch (error) {
        console.error("Error fetching personas:", error);
        this.checkboxOptions = [];
        this.showToast(this.$t('focusgroup.failedToLoadPersonasPleaseTryAgainLater'), "error");
      }
    },
    async addCampaign(campaignData) {
      try {
        console.log("Submitting campaign:", campaignData);
        const response = await add_campaign(
            campaignData.campaign_name,
            campaignData.description,
            this.selectedDomain
        );
        console.log("Topic added successfully:", response.data);
        this.showToast(this.$t('focusgroup.topicAddedSuccessfully'), "success");
        await this.fetchCampaigns();
      } catch (error) {
        console.error("Error adding campaign:", error);
        this.showToast(this.$t('focusgroup.failedToAddCampaignPleaseTryAgain'), "error");
      }
    },
    formatTimestamp(timestamp) {
      return formatDateTime(timestamp, this.$i18n.locale);
    },
    async fetchFocusGroups(selectedDomain) {
      this.isLoadingFocusGroups = true;
      this.focusGroups = [];
      this.selectedFocusGroup = null;
      this.selectedGroup = null;
      try {
        const response = await fetch_focus_groups(selectedDomain);
        if (response && Array.isArray(response.data)) {
          this.focusGroups = response.data;
          console.log("Fetched focus groups:", this.focusGroups);
        } else {
          throw new Error("Invalid response format");
        }
      } catch (error) {
        console.error("Error fetching focus groups:", error);
        this.focusGroups = [];
        this.showToast(this.$t('focusgroup.failedToLoadFocusGroupsPleaseTryAgainLater'), "error");
      } finally {
        this.isLoadingFocusGroups = false;
      }
    },
    async loadFocusGroup(focus_group_id) {
      this.isLoadingFocusGroupDetails = true;
      try {
        const response = await get_focus_group(focus_group_id);
        console.log("focus_group_id:", focus_group_id);

        if (response && response.data) {
          const focusGroupData = response.data;

          console.log("Raw focus group data:", focusGroupData);
          console.log("Image data map:", focusGroupData.image_data_map);
          console.log("Histories before processing:", focusGroupData.histories);

          const normalize = (s) =>
              (s ?? '')
                  .toString()
                  .toLowerCase()
                  .replace(/\s+/g, ' ')
                  .replace(/[?.!,:;()[\]"''`´]/g, '')
                  .trim();

          // 1) Costruisci mappa domanda -> image_id
          const qMap = new Map();
          if (Array.isArray(focusGroupData.questions)) {
            focusGroupData.questions.forEach((q) => {
              if (q?.image_ids?.length) {
                const imgId = q.image_ids[0];
                if (q.question_id) {
                  qMap.set(`id:${q.question_id}`, imgId);
                }
                if (q.question_text) {
                  qMap.set(normalize(q.question_text), imgId);
                }
              }
            });
          }

          console.log("Question to image map:", Array.from(qMap.entries()));

          // 2) Inietta l'image_id e il numero di pagina nei messaggi dell'intervistatore se mancante
          if (Array.isArray(focusGroupData.histories)) {
            focusGroupData.histories = focusGroupData.histories.map((h) => {
              // 'facilitator' is the moderator name used by template-based runs
              if (!['interviewer', 'facilitator'].includes(h.persona_name)) return h;

              if (h.image_id) {
                console.log("History already has image_id:", h.image_id);
                // Estraggo il page number anche se l'image_id è già presente
                const match = h.image_id.match(/_page_(\d+)$/);
                const pageNumber = match ? match[1] : null;

                return { ...h, pdf_page_number: pageNumber };
              }

              let imageId = null;

              // a) match per question_id se disponibile
              if (h.question_id && qMap.has(`id:${h.question_id}`)) {
                imageId = qMap.get(`id:${h.question_id}`);
                console.log("Matched by question_id:", h.question_id, "->", imageId);
              }

              // b) match per testo normalizzato
              if (!imageId) {
                const msgKey = normalize(h.message);
                if (qMap.has(msgKey)) {
                  imageId = qMap.get(msgKey);
                  console.log("Matched by exact text ->", imageId);
                }
              }

              // c) fallback contains nei due versi
              if (!imageId) {
                const msgKey = normalize(h.message);
                for (const [k, v] of qMap.entries()) {
                  if (k.startsWith('id:')) continue;
                  if (msgKey.includes(k) || k.includes(msgKey)) {
                    imageId = v;
                    console.log("Matched by partial text ->", imageId);
                    break;
                  }
                }
              }

              // >>> LOGICA AGGIUNTA/MODIFICATA: Estrai il numero di pagina
              let pageNumber = null;
              if (imageId) {
                // L'ID è nel formato "nomefile.pdf_page_1", estraiamo il numero finale
                const match = imageId.match(/_page_(\d+)$/);
                if (match && match[1]) {
                  pageNumber = match[1];
                }
              }
              // <<< FINE LOGICA AGGIUNTA/MODIFICATA

              return imageId ? {
                ...h,
                image_id: imageId,
                pdf_page_number: pageNumber // Nuovo campo aggiunto
              } : h;
            });
          }

          // The image_id / pdf_page_number injected above onto the primary
          // `histories` are matched by question text; the translated language
          // versions in `histories_i18n` have the same entries in the same
          // order, so copy that resolved metadata across by index (no re-match).
          if (focusGroupData.histories_i18n && typeof focusGroupData.histories_i18n === 'object'
              && Array.isArray(focusGroupData.histories)) {
            Object.keys(focusGroupData.histories_i18n).forEach((lang) => {
              const arr = focusGroupData.histories_i18n[lang];
              if (!Array.isArray(arr)) return;
              focusGroupData.histories_i18n[lang] = arr.map((h, i) => {
                const src = focusGroupData.histories[i];
                if (!src) return h;
                const merged = { ...h };
                if (src.image_id) merged.image_id = src.image_id;
                if (src.pdf_page_number) merged.pdf_page_number = src.pdf_page_number;
                if (src.question_id && !merged.question_id) merged.question_id = src.question_id;
                return merged;
              });
            });
          }

          this.selectedFocusGroup = focusGroupData;
          console.log("Final focus group with injected image_ids and page numbers:", this.selectedFocusGroup.histories);

        } else {
          throw new Error("Focus group not found");
        }
      } catch (error) {
        console.error("Error fetching focus group:", error);
        this.showToast(this.$t('focusgroup.failedToLoadFocusGroupDetails'), "error");
      } finally {
        this.isLoadingFocusGroupDetails = false;
      }
    },
    promptDeleteFocusGroup(group) {
      this.focusGroupToDelete = group;
      this.showDeleteConfirm = true;
    },
    async confirmDeleteFocusGroup() {
      if (!this.focusGroupToDelete) return;
      this.deletingFocusGroup = true;
      try {
        await delete_focus_group(this.focusGroupToDelete.id);
        this.focusGroups = this.focusGroups.filter(g => g.id !== this.focusGroupToDelete.id);
        if (this.selectedGroup?.id === this.focusGroupToDelete.id) {
          this.selectedGroup = null;
          this.selectedFocusGroup = null;
        }
        this.showToast(this.$t('focusgroup.focusGroupDeletedSuccessfully'), "success");
      } catch (error) {
        console.error("Error deleting focus group:", error);
        this.showToast(this.$t('focusgroup.failedToDeleteFocusGroup'), "error");
      } finally {
        this.deletingFocusGroup = false;
        this.showDeleteConfirm = false;
        this.focusGroupToDelete = null;
      }
    },
    handleFocusGroupClick(group) {
      console.log("Focus group clicked:", group);
      this.selectedGroup = group;
      this.loadFocusGroup(group.id);
    },
    convertMarkdown(text) {
      if (!text) return '';
      return marked(text);
    },
    getPersonaSummary(persona) {
      const summary = this.displaySummary;
      if (summary && summary[persona]) {
        return summary[persona];
      }
      return null;
    },
    async downloadPlan(){
      const element = this.$refs.improvementPlanContainer;

      const clone = element.cloneNode(true);
      clone.style.maxHeight = "none";
      clone.style.overflow = "visible";
      clone.style.height = "auto";
      clone.style.width = element.offsetWidth + "px";
      clone.classList.add("pdf-export");

      const hiddenContainer = document.createElement("div");
      hiddenContainer.style.position = "absolute";
      hiddenContainer.style.left = "-9999px";
      hiddenContainer.appendChild(clone);
      document.body.appendChild(hiddenContainer);

      const scale = 1.0;
      const canvas = await html2canvas(clone, {
        scale,
        useCORS: true,
        backgroundColor: "#ffffff",
      }).catch((err) => console.error("html2canvas error:", err));

      document.body.removeChild(hiddenContainer);

      const imgData = canvas.toDataURL("image/png");
      const pdfWidth = 595.28;
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      const pdf = new jsPDF({
        orientation: "p",
        unit: "pt",
        format: [pdfWidth, pdfHeight],
      });

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.setFontSize(12);

      pdf.save(`ImprovementPlan.pdf`);
    },
  },
  async created() {
    try {
      await Promise.all([
        this.fetchCampaigns(this.selectedDomain),
        this.fetchPersonaNames(this.selectedDomain),
        this.fetchFocusGroups(this.selectedDomain),
      ]);
    } catch (error) {
      console.error("Error initializing component:", error);
      this.showToast(this.$t('focusgroup.failedToLoadDataPleaseTryAgainLater'), "error");
    }
  },
  beforeUnmount() {
    this.focus_group_images.forEach(img => {
      if (img.url && img.url.startsWith('blob:')) {
        URL.revokeObjectURL(img.url);
      }
    });
  }
};
</script>

<style scoped>
.focus-groups-list {
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
}

.focus-groups-list::-webkit-scrollbar {
  width: 6px;
}

.focus-groups-list::-webkit-scrollbar-track {
  background-color: transparent;
}

.focus-groups-list::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 6px;
}

.focus-groups-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.2);
}

.focus-group-item {
  transition: all 0.2s ease;
  margin-bottom: 6px !important;
}

.focus-group-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.05);
}

.focus-group-details-card {
  transition: all 0.3s ease;
}

.expansion-panel {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px !important;
  overflow: hidden;
  transition: all 0.2s ease;
}

.expansion-panel:hover {
  border-color: rgba(var(--v-theme-primary), 0.2);
}

.panel-title {
  border-bottom: none;
}

.panel-text {
  background-color: #f5f8ff;
}

.stimulus-material {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
}

.stimulus-material-item + .stimulus-material-item {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.markdown-content {
  line-height: 1.7;
  color: #333;
  padding: 1.5rem;
  background-color: #ffffff;
  border-radius: 12px;
  border: 1px solid #d0e0ff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

.markdown-content :deep(h1) {
  font-size: 2rem;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
  margin-top: 0;
  margin-bottom: 24px;
  padding-bottom: 10px;
  border-bottom: 3px solid rgba(var(--v-theme-primary), 0.3);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.markdown-content :deep(h2) {
  font-size: 1.6rem;
  font-weight: 600;
  color: #2a65b5;
  margin-top: 36px;
  margin-bottom: 18px;
  padding-bottom: 8px;
  border-bottom: 1px solid #c8daff;
}

.markdown-content :deep(h3) {
  font-size: 1.35rem;
  font-weight: 600;
  color: rgba(var(--v-theme-primary), 0.8);
  margin-top: 28px;
  margin-bottom: 14px;
}

.markdown-content :deep(p) {
  margin-bottom: 16px;
  color: #4a4a4a;
}

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  padding-left: 30px;
  margin-bottom: 18px;
}

.markdown-content :deep(li) {
  margin-bottom: 10px;
  line-height: 1.6;
  color: #4a4a4a;
}

.markdown-content :deep(li::marker) {
  color: rgba(var(--v-theme-primary), 0.7);
  font-weight: bold;
  font-size: 1.1em;
}

.markdown-content :deep(strong) {
  color: #1a4f90;
  font-weight: 700;
}

.markdown-content :deep(blockquote) {
  margin-left: 0;
  padding: 16px 24px;
  background-color: rgba(var(--v-theme-primary), 0.08);
  border-left: 5px solid rgb(var(--v-theme-primary));
  color: #3a3a3a;
  font-style: italic;
  border-radius: 0 10px 10px 0;
  margin-top: 20px;
  margin-bottom: 20px;
}

.markdown-content :deep(code) {
  background-color: #e0f2f7;
  color: #00796b;
  padding: 4px 7px;
  border-radius: 5px;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
  white-space: nowrap;
}

.markdown-content :deep(pre) {
  background-color: #2d2d2d;
  color: #f8f8f2;
  padding: 16px;
  border-radius: 8px;
  overflow-x: auto;
  font-family: 'Courier New', monospace;
  font-size: 0.85em;
  margin-top: 20px;
  margin-bottom: 20px;
}

.markdown-content :deep(pre code) {
  background-color: transparent;
  color: inherit;
  padding: 0;
}

.no-data {
  font-style: italic;
  color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: #e3f2fd;
  border-radius: 8px;
  border: 1px solid #90caf9;
  color: #1976d2;
}

.custom-panels :deep(.v-expansion-panel-title) {
  min-height: 56px;
}

.border {
  border: 1px solid #bbdefb;
}

.v-card {
  animation: fade-in 0.3s ease-in-out;
}

@keyframes fade-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.loading-text {
  margin-top: 16px;
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.6);
}

@keyframes pulse {
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
}

.pulse {
  animation: pulse 1.5s infinite ease-in-out;
}
</style>